import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import { getFirestore, type Firestore } from 'firebase/firestore'
import { getAnalytics, isSupported, type Analytics } from 'firebase/analytics'
import { getRemoteConfig, type RemoteConfig } from 'firebase/remote-config'

export type BenjarukHomeFirebaseApi = {
  app: FirebaseApp
  db: Firestore
  analytics: Analytics | null
  remoteConfig: RemoteConfig
  ready: Promise<void>
}

declare global {
  interface Window {
    firebaseConfig?: Record<string, string>
    BenjarukHomeFirebase?: BenjarukHomeFirebaseApi
    BenjarukFirebase?: BenjarukHomeFirebaseApi
  }
}

let app: FirebaseApp | null = null
let db: Firestore | null = null
let analytics: Analytics | null = null
let remoteConfig: RemoteConfig | null = null
let readyPromise: Promise<void> | null = null

function readConfig(): Record<string, string> {
  const cfg = window.firebaseConfig
  if (!cfg || !cfg.apiKey || !cfg.projectId) {
    throw new Error('[home-v2] window.firebaseConfig is missing')
  }
  return cfg
}

export function getApp(): FirebaseApp {
  if (!app) {
    const existing = getApps()
    app = existing.length ? existing[0]! : initializeApp(readConfig())
  }
  return app
}

export function getDb(): Firestore {
  if (!db) db = getFirestore(getApp())
  return db
}

export function getFirebaseRemoteConfig(): RemoteConfig {
  if (!remoteConfig) {
    remoteConfig = getRemoteConfig(getApp())
    remoteConfig.settings = {
      minimumFetchIntervalMillis: 3600000,
      fetchTimeoutMillis: 10000,
    }
    remoteConfig.defaultConfig = {
      show_coupon: false,
      show_random_products: false,
      home_message_variant: 'control',
    }
  }
  return remoteConfig
}

export async function getFirebaseAnalytics(): Promise<Analytics | null> {
  if (analytics) return analytics
  try {
    if (await isSupported()) {
      analytics = getAnalytics(getApp())
    }
  } catch {
    analytics = null
  }
  return analytics
}

export function getReady(): Promise<void> {
  if (!readyPromise) {
    readyPromise = (async () => {
      getApp()
      getDb()
      getFirebaseRemoteConfig()
      await getFirebaseAnalytics()
    })()
  }
  return readyPromise
}

export function exposeBenjarukHomeFirebase(
  extra?: Partial<BenjarukHomeFirebaseApi>,
): BenjarukHomeFirebaseApi {
  const api: BenjarukHomeFirebaseApi = {
    app: getApp(),
    db: getDb(),
    analytics,
    remoteConfig: getFirebaseRemoteConfig(),
    ready: getReady(),
    ...extra,
  }
  window.BenjarukHomeFirebase = api
  // Shared name used by some shared helpers; home has no auth/storage.
  window.BenjarukFirebase = api
  return api
}
